import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Code, Eye, FileCode2, ImagePlus, ScrollText } from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import { htmlToMarkdown, markdownToHtml } from '../utils/guideContentConversion';
import { richContentBodyStyles } from './richContentBodyStyles';

const EditorShell = styled.div`
  display: grid;
  gap: 14px;
`;

const ModeTabs = styled.div`
  display: inline-flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ModeTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid ${props => props.$active ? '#2c9fa4' : '#d1d5db'};
  background: ${props => props.$active ? '#f0fdfa' : 'white'};
  color: ${props => props.$active ? '#115e59' : '#334155'};
  border-radius: 999px;
  padding: 10px 14px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #2c9fa4;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const ConversionNote = styled.div`
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
`;

const WarningNote = styled.div`
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #fde68a;
  background: #fffbeb;
  color: #92400e;
  font-size: 13px;
  line-height: 1.6;
`;

const MarkdownPanel = styled.div`
  display: grid;
  gap: 10px;
`;

const MarkdownToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

const MarkdownButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: white;
  color: #334155;
  font: inherit;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #2c9fa4;
    color: #115e59;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const MarkdownEditor = styled.textarea`
  width: 100%;
  min-height: ${props => `${props.$height}px`};
  max-height: ${props => `${Math.max(props.$height + 80, props.$height)}px`};
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #d1d5db;
  background: #f8fafc;
  color: #0f172a;
  font-family: 'Consolas', 'SFMono-Regular', 'Liberation Mono', Menlo, monospace;
  font-size: 14px;
  line-height: 1.65;
  resize: vertical;
  overflow-y: auto;
  white-space: pre-wrap;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.12);
  }
`;

const HelperText = styled.div`
  color: #64748b;
  font-size: 12px;
  line-height: 1.6;
`;

const HtmlPanel = styled.div`
  display: grid;
  gap: 10px;
`;

const HtmlSourceEditor = styled.textarea`
  width: 100%;
  min-height: ${props => `${props.$height}px`};
  max-height: ${props => `${Math.max(props.$height + 80, props.$height)}px`};
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #d1d5db;
  background: #f8fafc;
  color: #0f172a;
  font-family: 'Consolas', 'SFMono-Regular', 'Liberation Mono', Menlo, monospace;
  font-size: 14px;
  line-height: 1.65;
  resize: vertical;
  overflow-y: auto;
  white-space: pre-wrap;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.12);
  }
`;

const PreviewPanel = styled.div`
  display: grid;
  gap: 10px;
`;

const PreviewSurface = styled.div`
  min-height: ${props => `${props.$height}px`};
  max-height: ${props => `${Math.max(props.$height + 80, props.$height)}px`};
  overflow-y: auto;
  padding: 20px;
  border-radius: 16px;
  border: 1px solid #dbe4ea;
  background: white;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
`;

const PreviewBody = styled.div`
  ${richContentBodyStyles}
`;

const PreviewEmptyState = styled.div`
  color: #64748b;
  font-size: 14px;
  line-height: 1.7;
`;

const DEFAULT_MARKDOWN_PLACEHOLDER = `# Heading

Use markdown here.

| Column | Value |
| --- | --- |
| Example | Table row |

- Lists
- **Bold text**
- \`inline code\``;

const DEFAULT_CONVERSION_NOTE = 'Switch between Rich Text, raw HTML, Markdown, and Preview. The saved value stays HTML, so Markdown and Rich Text both flow into the published markup. HTML-only formatting may be simplified if you later route it back through Rich Text or Markdown.';

const DEFAULT_HTML_HINT = 'Use raw HTML here when you need markup that the rich-text editor cannot safely preserve, such as custom tables.';

const DEFAULT_TABLE_PROTECTION_HINT = 'This content includes a table. Rich Text mode is temporarily disabled because it can strip table markup. Keep editing the table in HTML or Markdown until you remove it.';

const DEFAULT_MARKDOWN_HINT = 'Use headings, lists, links, blockquotes, code blocks, images, and GitHub-style tables here. The saved content is still converted into HTML automatically.';

const DEFAULT_PREVIEW_HINT = 'This is the rendered preview of the saved HTML.';

const DEFAULT_PREVIEW_EMPTY_STATE = 'Nothing to preview yet. Start writing in Rich Text, HTML, or Markdown and the rendered content will appear here.';

const DEFAULT_MARKDOWN_IMAGE_HINT = 'Uploads media and inserts standard markdown image syntax at the cursor.';

const containsHtmlTable = (value) => /<table[\s>]/i.test(String(value || ''));

const inferAltText = (fileName) => String(fileName || '')
  .replace(/\.[^.]+$/, '')
  .replace(/[-_]+/g, ' ')
  .trim() || 'Uploaded image';

const RichContentEditor = ({
  htmlValue,
  onChange,
  onImageUpload,
  disabled = false,
  height = 320,
  richTextPlaceholder = 'Write the content here...',
  htmlSourcePlaceholder = '<p>Write the content HTML here...</p>',
  markdownPlaceholder = DEFAULT_MARKDOWN_PLACEHOLDER,
  conversionNote = DEFAULT_CONVERSION_NOTE,
  htmlHint = DEFAULT_HTML_HINT,
  tableProtectionHint = DEFAULT_TABLE_PROTECTION_HINT,
  markdownHint = DEFAULT_MARKDOWN_HINT,
  previewHint = DEFAULT_PREVIEW_HINT,
  previewEmptyState = DEFAULT_PREVIEW_EMPTY_STATE,
  markdownImageHint = DEFAULT_MARKDOWN_IMAGE_HINT,
}) => {
  const normalizedHtmlValue = String(htmlValue || '');
  const [editorMode, setEditorMode] = useState(() => (
    containsHtmlTable(normalizedHtmlValue) ? 'html' : 'rich-text'
  ));
  const [markdownValue, setMarkdownValue] = useState(() => htmlToMarkdown(normalizedHtmlValue));
  const markdownTextareaRef = useRef(null);
  const markdownUploadInputRef = useRef(null);
  const previousHtmlValueRef = useRef(normalizedHtmlValue);
  const isApplyingMarkdownChangeRef = useRef(false);
  const hasMarkdownImageUpload = typeof onImageUpload === 'function';
  const hasHtmlTable = useMemo(() => containsHtmlTable(normalizedHtmlValue), [normalizedHtmlValue]);

  useEffect(() => {
    const nextHtmlValue = normalizedHtmlValue;
    const previousHtmlValue = previousHtmlValueRef.current;
    previousHtmlValueRef.current = nextHtmlValue;

    if (isApplyingMarkdownChangeRef.current) {
      isApplyingMarkdownChangeRef.current = false;
      return;
    }

    if (editorMode === 'markdown' && nextHtmlValue !== previousHtmlValue) {
      setMarkdownValue(htmlToMarkdown(nextHtmlValue));
    }
  }, [editorMode, normalizedHtmlValue]);

  useEffect(() => {
    if (hasHtmlTable && editorMode === 'rich-text') {
      setEditorMode('html');
    }
  }, [editorMode, hasHtmlTable]);

  const updateFromMarkdown = (nextMarkdownValue) => {
    setMarkdownValue(nextMarkdownValue);
    isApplyingMarkdownChangeRef.current = true;
    onChange(markdownToHtml(nextMarkdownValue));
  };

  const handleModeChange = (nextMode) => {
    if (nextMode === editorMode) {
      return;
    }

    if (nextMode === 'rich-text' && hasHtmlTable) {
      setEditorMode('html');
      return;
    }

    if (nextMode === 'markdown') {
      setMarkdownValue(htmlToMarkdown(normalizedHtmlValue));
    }

    setEditorMode(nextMode);
  };

  const insertMarkdownSnippet = (snippet) => {
    const textarea = markdownTextareaRef.current;

    if (!textarea) {
      updateFromMarkdown(`${markdownValue}${markdownValue ? '\n' : ''}${snippet}`);
      return;
    }

    const selectionStart = textarea.selectionStart ?? markdownValue.length;
    const selectionEnd = textarea.selectionEnd ?? markdownValue.length;
    const nextMarkdownValue = `${markdownValue.slice(0, selectionStart)}${snippet}${markdownValue.slice(selectionEnd)}`;
    const nextCursorPosition = selectionStart + snippet.length;

    updateFromMarkdown(nextMarkdownValue);

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(nextCursorPosition, nextCursorPosition);
    });
  };

  const handleMarkdownImageUpload = async (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile || typeof onImageUpload !== 'function') {
      return;
    }

    try {
      const imageUrl = await onImageUpload(selectedFile);

      if (!imageUrl) {
        return;
      }

      const snippet = `![${inferAltText(selectedFile.name)}](${imageUrl})`;
      insertMarkdownSnippet(snippet);
    } catch (error) {
      console.error('Failed to upload markdown image:', error);
      window.alert('Failed to upload image. Please try again.');
    } finally {
      event.target.value = '';
    }
  };

  return (
    <EditorShell>
      <ModeTabs>
        <ModeTab
          type="button"
          $active={editorMode === 'rich-text'}
          onClick={() => handleModeChange('rich-text')}
          disabled={disabled || hasHtmlTable}
          title={hasHtmlTable ? 'Edit table content in HTML or Markdown mode.' : undefined}
        >
          <ScrollText size={16} />
          Rich Text
        </ModeTab>
        <ModeTab
          type="button"
          $active={editorMode === 'html'}
          onClick={() => handleModeChange('html')}
          disabled={disabled}
        >
          <Code size={16} />
          HTML
        </ModeTab>
        <ModeTab
          type="button"
          $active={editorMode === 'markdown'}
          onClick={() => handleModeChange('markdown')}
          disabled={disabled}
        >
          <FileCode2 size={16} />
          Markdown
        </ModeTab>
        <ModeTab
          type="button"
          $active={editorMode === 'preview'}
          onClick={() => handleModeChange('preview')}
          disabled={disabled}
        >
          <Eye size={16} />
          Preview
        </ModeTab>
      </ModeTabs>

      <ConversionNote>{conversionNote}</ConversionNote>

      {hasHtmlTable && (
        <WarningNote>{tableProtectionHint}</WarningNote>
      )}

      {editorMode === 'rich-text' ? (
        <RichTextEditor
          value={normalizedHtmlValue}
          onChange={onChange}
          placeholder={richTextPlaceholder}
          height={height}
          onImageUpload={onImageUpload}
          disabled={disabled}
        />
      ) : editorMode === 'html' ? (
        <HtmlPanel>
          <HelperText>{htmlHint}</HelperText>
          <HtmlSourceEditor
            value={normalizedHtmlValue}
            onChange={(event) => onChange(event.target.value)}
            placeholder={htmlSourcePlaceholder}
            $height={height}
            disabled={disabled}
            spellCheck={false}
          />
        </HtmlPanel>
      ) : editorMode === 'markdown' ? (
        <MarkdownPanel>
          {hasMarkdownImageUpload && (
            <MarkdownToolbar>
              <MarkdownButton
                type="button"
                onClick={() => markdownUploadInputRef.current?.click()}
                disabled={disabled}
              >
                <ImagePlus size={16} />
                Upload Image
              </MarkdownButton>
              <HelperText>{markdownImageHint}</HelperText>
            </MarkdownToolbar>
          )}

          <MarkdownEditor
            ref={markdownTextareaRef}
            value={markdownValue}
            onChange={(event) => updateFromMarkdown(event.target.value)}
            placeholder={markdownPlaceholder}
            $height={height}
            disabled={disabled}
            spellCheck={false}
          />

          <HelperText>{markdownHint}</HelperText>

          <input
            ref={markdownUploadInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={handleMarkdownImageUpload}
            style={{ display: 'none' }}
          />
        </MarkdownPanel>
      ) : (
        <PreviewPanel>
          <HelperText>{previewHint}</HelperText>
          <PreviewSurface $height={height}>
            {normalizedHtmlValue.trim() ? (
              <PreviewBody dangerouslySetInnerHTML={{ __html: normalizedHtmlValue }} />
            ) : (
              <PreviewEmptyState>{previewEmptyState}</PreviewEmptyState>
            )}
          </PreviewSurface>
        </PreviewPanel>
      )}
    </EditorShell>
  );
};

export default RichContentEditor;
